import React from 'react'
import axios from 'axios'
import { Dialog, DialogContent, CircularProgress, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { SourceChip } from './SourceChip'

const CATEGORIES = ['general', 'irrigation', 'plant', 'aggregate', 'lighting', 'hardscape', 'herbicide', 'fertilizer']

const fmt$ = (v) => v != null ? '$' + Number(v).toFixed(2) : ''
const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
const fmtQty = (v) => Number(v || 0).toLocaleString('en-US')

function buildLinkBody (item) {
  switch (item.source) {
    case 'catalog': return { materialCatalogItemId: item.materialCatalogItemId || item._id }
    case 'aspire': return { aspireItemId: item.aspireItemId }
    case 'vendor': return { vendorCatalogProductId: item.vendorCatalogProductId }
    case 'legacy': return { legacyTakeoffId: item.legacyTakeoffId }
    case 'aggregate': return { aggregateMaterialProfileId: item.aggregateMaterialProfileId, aggregateGradationId: item.aggregateGradationId }
    default: return { materialCatalogItemId: item._id }
  }
}

function StripedPlaceholder ({ size = 80, label }) {
  return (
    <div style={{ width: size, height: size, position: 'relative' }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} preserveAspectRatio="none">
        <defs>
          <pattern id="stripes-ph" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(28,36,25,0.10)" strokeWidth="3" />
          </pattern>
        </defs>
        <rect width={size} height={size} fill="url(#stripes-ph)" />
      </svg>
      {label && <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--ink-4)', textAlign: 'center', lineHeight: 1.3, padding: 4 }}>{label}</span>}
    </div>
  )
}

function DetField ({ label, value, mono }) {
  return (
    <div className="det-field">
      <span className="det-label">{label}</span>
      <span className={`det-value${mono ? ' mono' : ''}`}>{value}</span>
    </div>
  )
}

function PriceTier ({ label, value, primary, dim }) {
  return (
    <div className={`price-tier${primary ? ' price-tier--primary' : ''}${dim ? ' price-tier--dim' : ''}`}>
      <span className="det-label">{label}</span>
      <span className="price-val">{fmt$(value)}</span>
    </div>
  )
}

function DialogHeader ({ material, catalogProduct, imgError, setImgError, onClose }) {
  const opt = material?.vendorOptions?.[0]
  const [detOpen, setDetOpen] = React.useState(true)

  const d = catalogProduct || null
  const img = d?.media?.find(m => m.isPrimary)?.url || d?.media?.[0]?.url
  const pricing = d?.pricing
  const hasRichData = !!d

  return (
    <>
      <div className="dlg-head-bar">
        <span className="dlg-overline">Linking material to catalog</span>
        <h3 className="dlg-title">Find a catalog match for this material</h3>
      </div>

      <div className="dlg-source-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ marginBottom: 4 }}>
              {material.vendorOptions?.map((vo, i) => (
                <SourceChip key={i} kind="quote" label={vo.vendor} size="xs" />
              ))}
            </div>
            <div className="dlg-source-name">
              {material.canonicalName}
              {d?.externalUrl && <span className="live-pip">● LIVE</span>}
            </div>
            <div className="dlg-source-meta">
              <span>{material.category}</span>
              <span className="dot-sep">·</span>
              <span className="mono">{fmtQty(material.totalQty)} {material.unit}</span>
              {opt && (
                <>
                  <span className="dot-sep">·</span>
                  <span className="mono">{fmt$(opt.unitPrice)}/{opt.uom || material.unit}</span>
                  <span className="dot-sep">·</span>
                  <span className="mono">{opt.vendorSku}</span>
                  <span className="dot-sep">·</span>
                  <span>from <b>{opt.vendor}</b></span>
                </>
              )}
            </div>
            {material.notes && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ink-2)', background: 'var(--bg-sunken, #f0ede6)', padding: '4px 8px', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
                <span style={{ fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3)', marginRight: 6 }}>Notes:</span>
                {material.notes}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--ink-3)', cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}
          >×</button>
        </div>
      </div>

      {hasRichData && detOpen && (
        <div className="src-det">
          <div className="src-det-grid">
            <div className="src-det-img">
              {img && !imgError
                ? <img src={img} alt="" onError={() => setImgError(true)} />
                : <StripedPlaceholder size={80} label={`product image\nfrom ${opt?.vendor || ''}`} />
              }
            </div>
            <div className="src-det-body">
              {d.description && <p className="src-det-desc">{d.description}</p>}
              <div className="src-det-fields">
                {d.manufacturer && <DetField label="Manufacturer" value={d.manufacturer} />}
                {d.manufacturerPartNumber && <DetField label="MPN" value={d.manufacturerPartNumber} mono />}
                {d.category && <DetField label="Vendor category" value={cap(d.category)} />}
                {d.vendorItemNumber && <DetField label="Vendor SKU" value={d.vendorItemNumber} mono />}
              </div>
              {pricing && (
                <div className="pricing-tiers pricing-tiers-source">
                  {pricing.ourPrice != null && <PriceTier label="Our price" value={pricing.ourPrice} primary />}
                  {pricing.accountPrice != null && <PriceTier label="Account" value={pricing.accountPrice} />}
                  {pricing.regularPrice != null && <PriceTier label="List" value={pricing.regularPrice} />}
                  {opt?.unitPrice != null && <PriceTier label="On quote" value={opt.unitPrice} dim />}
                </div>
              )}
            </div>
          </div>
          <button className="src-det-collapse" onClick={() => setDetOpen(false)}>▴ Hide details</button>
        </div>
      )}

      {hasRichData && !detOpen && (
        <button className="src-det-reveal" onClick={() => setDetOpen(true)}>
          ▾ Show what we know about this item — {opt?.vendor} catalog
        </button>
      )}
    </>
  )
}

function MultiSelectGroup ({ kind, title, subtitle, items, empty, onBulkSelect, linking, defaultOpen }) {
  const [open, setOpen] = React.useState(defaultOpen != null ? defaultOpen : (items && items.length > 0))
  const [selected, setSelected] = React.useState(new Set())
  const count = items?.length || 0

  const toggleOne = (id) => setSelected(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })
  const toggleAll = () => setSelected(prev => prev.size === count ? new Set() : new Set(items.map(i => i._id)))

  return (
    <div className="grp-block">
      <button className="grp-head" onClick={() => setOpen(!open)}>
        <span className="grp-chev">{open ? '▾' : '▸'}</span>
        <SourceChip kind={kind} size="xs" />
        <span className="grp-title">{title}</span>
        <span className="grp-sub">{subtitle}</span>
        <span className="grp-count">{count} {count === 1 ? 'match' : 'matches'}</span>
      </button>
      {open && (
        <div className="grp-body">
          {count === 0 && <div className="grp-empty">{empty}</div>}
          {count > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', borderBottom: '1px solid var(--line-1)', fontSize: 12 }}>
              <input type="checkbox" checked={selected.size === count} onChange={toggleAll} style={{ cursor: 'pointer' }} />
              <span style={{ color: 'var(--ink-3)' }}>
                {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
              </span>
              {selected.size > 0 && (
                <button
                  className="hmi-btn hmi-btn-primary hmi-btn-sm"
                  style={{ marginLeft: 'auto' }}
                  disabled={!!linking}
                  onClick={() => onBulkSelect(items.filter(i => selected.has(i._id)))}
                >
                  {linking === 'bulk-attach' ? 'Attaching...' : `Attach ${selected.size} items →`}
                </button>
              )}
            </div>
          )}
          {items?.map(item => {
            const priceHint = item.vendorPriceHints?.[0]
            const displayPrice = priceHint ? priceHint.unitCost : item.bestPrice
            return (
              <div key={item._id} className="res-row-wrap">
                <div className="res-row" style={{ opacity: linking && linking !== 'bulk-attach' ? 0.5 : 1 }}>
                  <input
                    type="checkbox"
                    checked={selected.has(item._id)}
                    onChange={() => toggleOne(item._id)}
                    style={{ cursor: 'pointer', marginRight: 4, flexShrink: 0 }}
                  />
                  <div className="res-main">
                    <div className="res-name">
                      {item.canonicalName}
                      {priceHint && <span className="res-vendor"> · {priceHint.vendor}</span>}
                    </div>
                    <div className="res-meta">
                      {item.category && <><span>{cap(item.category)}</span><span className="dot-sep">·</span></>}
                      <span className="mono">{item.unit}</span>
                      {displayPrice != null && <><span className="dot-sep">·</span><span className="mono">{fmt$(displayPrice)}</span></>}
                      {priceHint && <><span className="dot-sep">·</span><span>{priceHint.vendor}</span></>}
                    </div>
                  </div>
                  <div className="res-action">
                    <button
                      className="hmi-btn hmi-btn-outline hmi-btn-sm"
                      disabled={!!linking}
                      onClick={() => onBulkSelect([item])}
                      style={{ fontSize: 11, padding: '2px 8px' }}
                    >
                      Attach →
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Group ({ kind, title, subtitle, items, empty, onSelect, linking, showPromote, defaultOpen }) {
  const [open, setOpen] = React.useState(defaultOpen != null ? defaultOpen : (items && items.length > 0))
  const count = items?.length || 0

  return (
    <div className="grp-block">
      <button className="grp-head" onClick={() => setOpen(!open)}>
        <span className="grp-chev">{open ? '▾' : '▸'}</span>
        <SourceChip kind={kind} size="xs" />
        <span className="grp-title">{title}</span>
        <span className="grp-sub">{subtitle}</span>
        <span className="grp-count">{count} {count === 1 ? 'match' : 'matches'}</span>
      </button>
      {open && (
        <div className="grp-body">
          {count === 0 && <div className="grp-empty">{empty}</div>}
          {items?.map(item => (
            <ResultRow key={item._id || item.aspireItemId || item.vendorCatalogProductId} item={item} kind={kind} onSelect={onSelect} linking={linking} showPromote={showPromote} />
          ))}
        </div>
      )}
    </div>
  )
}

function ResultRow ({ item, kind, onSelect, linking, showPromote }) {
  const [open, setOpen] = React.useState(false)
  const hasDetails = !!(item.imageUrl || item.details || item.externalUrl)
  const isActive = linking === (item._id || item.aspireItemId || item.vendorCatalogProductId)
  const priceHint = item.vendorPriceHints?.[0]
  const displayPrice = priceHint ? priceHint.unitCost : item.bestPrice
  const [imgErr, setImgErr] = React.useState(false)

  return (
    <div className={`res-row-wrap${open ? ' open' : ''}`}>
      <div className="res-row" style={{ opacity: linking && !isActive ? 0.5 : 1 }}>
        {hasDetails
          ? <button className="res-tw" onClick={() => setOpen(!open)}>{open ? '▾' : '▸'}</button>
          : <span className="res-tw res-tw--empty" />
        }
        {item.imageUrl && !imgErr && (
          <img src={item.imageUrl} alt="" className="res-thumb" onError={() => setImgErr(true)} />
        )}
        <div className={`res-main${hasDetails ? ' clickable' : ''}`} onClick={() => hasDetails && setOpen(!open)}>
          <div className="res-name">
            {item.canonicalName}
            {kind === 'vendor' && priceHint && <span className="res-vendor"> · {priceHint.vendor}</span>}
            {item.externalUrl && (
              <a href={item.externalUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ fontSize: 11, color: 'var(--ink-3)', textDecoration: 'none' }} title="View on vendor site">↗</a>
            )}
          </div>
          <div className="res-meta">
            {item.category && <><span>{cap(item.category)}</span><span className="dot-sep">·</span></>}
            <span className="mono">{item.unit}</span>
            {displayPrice != null && <><span className="dot-sep">·</span><span className="mono">{fmt$(displayPrice)}</span></>}
            {priceHint && <><span className="dot-sep">·</span><span>{priceHint.vendor}</span></>}
            {hasDetails && !open && (
              <><span className="dot-sep">·</span><button className="res-more" onClick={e => { e.stopPropagation(); setOpen(true) }}>Show details</button></>
            )}
          </div>
        </div>
        <div className="res-action">
          {isActive && <CircularProgress size={14} />}
          {!isActive && (
            <button
              className="hmi-btn hmi-btn-primary hmi-btn-sm"
              disabled={!!linking}
              onClick={() => onSelect(item)}
            >
              {showPromote ? 'Promote + link →' : 'Link →'}
            </button>
          )}
        </div>
      </div>
      {open && <ResultDetails item={item} kind={kind} />}
    </div>
  )
}

function ResultDetails ({ item, kind }) {
  const [imgErr, setImgErr] = React.useState(false)
  const d = item.details || {}
  const pricing = d.pricing

  return (
    <div className="res-det">
      <div className="res-det-grid">
        <div className="res-det-img">
          {item.imageUrl && !imgErr
            ? <img src={item.imageUrl} alt="" onError={() => setImgErr(true)} />
            : <StripedPlaceholder size={100} label="product image" />
          }
        </div>
        <div className="res-det-body">
          {pricing && (
            <div className="pricing-tiers">
              {pricing.our != null && <PriceTier label="Our price" value={pricing.our} primary />}
              {pricing.account != null && <PriceTier label="Account" value={pricing.account} />}
              {pricing.list != null && <PriceTier label="List" value={pricing.list} />}
              {pricing.quote != null && <PriceTier label="Quoted" value={pricing.quote} dim />}
            </div>
          )}

          <div className="res-det-fields">
            {kind === 'vendor' && item.vendorPriceHints?.[0]?.vendor && <DetField label="Vendor" value={item.vendorPriceHints[0].vendor} />}
            {(item.vendorSku || d.mpn) && <DetField label={kind === 'vendor' ? 'Vendor SKU' : 'Catalog ID'} value={item.vendorSku || d.mpn} mono />}
            {d.mfg && <DetField label="Manufacturer" value={d.mfg} />}
            {d.mpn && <DetField label="MPN" value={d.mpn} mono />}
            {d.category && <DetField label="Category" value={d.category} />}
            {d.catalogVersion && <DetField label="Catalog version" value={d.catalogVersion} mono />}
          </div>

          {d.description && (
            <div className="res-det-desc">
              <span className="dlg-overline">Description</span>
              <p>{d.description}</p>
            </div>
          )}

          {d.history?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <span className="dlg-overline">Recent purchase history · {d.history.length}</span>
              <table className="hist-table">
                <thead>
                  <tr><th>Date</th><th>Vendor</th><th>Project</th><th>Qty</th><th>Unit price</th></tr>
                </thead>
                <tbody>
                  {d.history.map((h, i) => (
                    <tr key={i}>
                      <td className="mono">{h.date}</td>
                      <td>{h.vendor}</td>
                      <td className="muted">{h.project}</td>
                      <td className="mono">{h.qty}</td>
                      <td className="mono">{fmt$(h.price)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const FALLBACK_UOMS = ['Each', 'LF', 'SF', 'SY', 'CY', 'TON', 'LB', 'GAL', 'Bag', 'Box', 'Pallet', 'Roll', 'Bundle', 'LS']

function CreateNewBlock ({ material, onCreateNew, linking, totalMatches, aspireRef }) {
  const purchaseUnits = aspireRef?.purchaseUnits?.length ? aspireRef.purchaseUnits : FALLBACK_UOMS
  const allocationUnits = aspireRef?.allocationUnits?.length ? aspireRef.allocationUnits : FALLBACK_UOMS
  const aspireCategories = aspireRef?.categories || []
  const opt = material?.vendorOptions?.[0]
  const [open, setOpen] = React.useState(false)
  const [name, setName] = React.useState(material?.canonicalName || '')
  const [cat, setCat] = React.useState('')
  const [subcat, setSubcat] = React.useState('')
  const [purchaseUom, setPurchaseUom] = React.useState(material?.unit || opt?.uom || '')
  const [allocationUom, setAllocationUom] = React.useState(material?.unit || opt?.uom || '')

  if (!open) {
    return (
      <div className="create-block">
        <div className="create-head">
          <span className="create-icon">+</span>
          <div className="create-text">
            <div className="create-title">Don't see a match?</div>
            <div className="create-sub">
              {totalMatches ? `None of the ${totalMatches} results above are right?` : 'No catalog has this material yet?'}
              {' '}Create a new item in our Material Catalog.
            </div>
          </div>
          <button className="hmi-btn hmi-btn-outline hmi-btn-sm" onClick={() => setOpen(true)}>Create new material →</button>
        </div>
      </div>
    )
  }

  return (
    <div className="create-block" style={{ borderStyle: 'solid', borderColor: 'var(--primary-soft)' }}>
      <span className="dlg-overline">New material catalog item</span>
      <div className="create-form">
        <TextField size="small" label="Item Name" variant="outlined" value={name} onChange={e => setName(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
        <Autocomplete
          size="small"
          options={aspireCategories.length > 0 ? aspireCategories.map(c => c.name) : CATEGORIES.map(cap)}
          value={cat || null}
          onChange={(_, v) => setCat(v || '')}
          freeSolo
          openOnFocus
          renderInput={(params) => <TextField {...params} label="Category" variant="outlined" />}
          style={{ flex: 1, minWidth: 160 }}
          classes={{ inputRoot: 'catalog-dlg-autocomplete' }}
        />
        <TextField size="small" label="Subcategory" variant="outlined" value={subcat} onChange={e => setSubcat(e.target.value)} placeholder="e.g. Valves, Pipe, Trees" style={{ flex: 1, minWidth: 120 }} />
      </div>
      <div className="create-form" style={{ marginTop: 8, alignItems: 'center' }}>
        <Autocomplete
          size="small"
          options={purchaseUnits}
          value={purchaseUom || null}
          onChange={(_, v) => setPurchaseUom(v || '')}
          freeSolo
          openOnFocus
          renderInput={(params) => <TextField {...params} label="Purchase Unit" variant="outlined" />}
          style={{ flex: 1, minWidth: 120 }}
          classes={{ inputRoot: 'catalog-dlg-autocomplete' }}
        />
        <span style={{ color: 'var(--ink-3)', fontSize: 14, fontWeight: 600, padding: '0 2px' }}>=</span>
        <TextField size="small" label="Allocation" variant="outlined" value="1.00" disabled style={{ width: 70 }} />
        <Autocomplete
          size="small"
          options={allocationUnits}
          value={allocationUom || null}
          onChange={(_, v) => setAllocationUom(v || '')}
          freeSolo
          openOnFocus
          renderInput={(params) => <TextField {...params} label="Allocation Unit" variant="outlined" />}
          style={{ flex: 1, minWidth: 120 }}
          classes={{ inputRoot: 'catalog-dlg-autocomplete' }}
        />
      </div>
      {material?.notes && (
        <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-2)', background: 'var(--bg-sunken, #f0ede6)', padding: '4px 8px', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-3)', marginRight: 6 }}>Plans notes:</span>
          {material.notes}
        </div>
      )}
      {opt && (
        <div className="create-vendor">
          <span className="dlg-overline">First vendor option (from this material)</span>
          <div className="create-vendor-card">
            <SourceChip kind="vendor" size="xs" />
            <b>{opt.vendor}</b>
            <span className="mono">{opt.vendorSku}</span>
            <span className="mono">{fmt$(opt.unitPrice)}/{opt.uom || material.unit}</span>
          </div>
        </div>
      )}
      <div className="create-actions">
        <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => setOpen(false)}>Back</button>
        <button
          className="hmi-btn hmi-btn-primary hmi-btn-sm"
          disabled={!!linking || !name.trim()}
          onClick={() => onCreateNew({ canonicalName: name.trim(), category: cat, subcategory: subcat, unit: purchaseUom, purchaseUom, allocationUom, vendor: opt?.vendor, unitPrice: opt?.unitPrice, vendorSku: opt?.vendorSku })}
        >
          {linking === 'create-new' ? 'Creating...' : 'Create + link →'}
        </button>
      </div>
    </div>
  )
}

const CatalogLinkDialog = ({ material: initialMaterial, materialId: initialMaterialId, projectId, url, onLinked, onClose, queue, queueIndex: initialQueueIndex, onSkipMaterial }) => {
  const [currentIdx, setCurrentIdx] = React.useState(initialQueueIndex >= 0 ? initialQueueIndex : 0)
  const isWizard = queue && queue.length > 1
  const activeMaterial = isWizard ? (queue[currentIdx] || initialMaterial) : initialMaterial
  const activeMaterialId = isWizard ? (activeMaterial?._id || initialMaterialId) : initialMaterialId

  const [search, setSearch] = React.useState(activeMaterial?.canonicalName || '')
  const [results, setResults] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [linking, setLinking] = React.useState(null)
  const [error, setError] = React.useState(null)
  const [catalogProduct, setCatalogProduct] = React.useState(null)
  const [imgError, setImgError] = React.useState(false)
  const [linkedIds, setLinkedIds] = React.useState(new Set())
  const [skippedIds, setSkippedIds] = React.useState(new Set())

  const [projectMaterials, setProjectMaterials] = React.useState([])
  const [vendorQuoteLines, setVendorQuoteLines] = React.useState([])
  const [aspireRef, setAspireRef] = React.useState(null)

  React.useEffect(() => {
    if (!projectId || !url) return
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/materials`)
      .then(res => setProjectMaterials(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
    axios.get(`${url}/api/bid-estimation/vendor-quotes/project/${projectId}/lines`)
      .then(res => setVendorQuoteLines(Array.isArray(res.data) ? res.data : []))
      .catch(() => {})
    Promise.all([
      axios.get(`${url}/api/bid-estimation/aspire-ref/unit-types`),
      axios.get(`${url}/api/bid-estimation/aspire-ref/categories`)
    ]).then(([uRes, cRes]) => {
      setAspireRef({
        purchaseUnits: uRes.data?.purchaseUnits || [],
        allocationUnits: uRes.data?.allocationUnits || [],
        categories: Array.isArray(cRes.data) ? cRes.data : []
      })
    }).catch(() => {})
  }, [projectId, url])

  const opt = activeMaterial?.vendorOptions?.[0]

  const resetForMaterial = React.useCallback((m) => {
    setSearch(m?.canonicalName || '')
    setResults([])
    setError(null)
    setLinking(null)
    setCatalogProduct(null)
    setImgError(false)
  }, [])

  React.useEffect(() => {
    resetForMaterial(activeMaterial)
  }, [currentIdx])

  React.useEffect(() => {
    if (!opt?.vendorSku || !url) return
    setCatalogProduct(null)
    setImgError(false)
    axios.get(`${url}/api/vendor-catalogs/products/search`, { params: { q: opt.vendorSku, limit: 1 } })
      .then(res => {
        const found = Array.isArray(res.data?.results) ? res.data.results : []
        const match = found.find(p =>
          String(p.vendorItemNumber || '').replace(/\s/g, '').toLowerCase() === String(opt.vendorSku).replace(/\s/g, '').toLowerCase()
        )
        if (match) setCatalogProduct(match)
      })
      .catch(() => {})
  }, [opt?.vendorSku, url, currentIdx])

  React.useEffect(() => {
    if (!search.trim() || search.trim().length < 2) { setResults([]); return }
    const timer = setTimeout(() => {
      setLoading(true)
      axios.get(`${url}/api/bid-estimation/unified-catalog`, { params: { search: search.trim(), limit: 30 } })
        .then(res => { setResults(Array.isArray(res.data) ? res.data : []); setLoading(false) })
        .catch(() => { setResults([]); setLoading(false) })
    }, 300)
    return () => clearTimeout(timer)
  }, [search, url])

  const advanceToNext = React.useCallback(() => {
    if (!isWizard) return
    for (let i = currentIdx + 1; i < queue.length; i++) {
      const id = queue[i]?._id
      if (!linkedIds.has(id) && !skippedIds.has(id)) {
        setCurrentIdx(i)
        return
      }
    }
    onLinked()
  }, [isWizard, currentIdx, queue, linkedIds, skippedIds, onLinked])

  const handleSelect = (item) => {
    const id = item._id || item.aspireItemId || item.vendorCatalogProductId
    setLinking(id)
    setError(null)
    const body = buildLinkBody(item)
    if (opt) {
      body.vendorEnrichment = {
        vendor: opt.vendor,
        vendorSku: opt.vendorSku,
        unitPrice: opt.unitPrice,
        uom: opt.uom
      }
    }
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${activeMaterialId}/promote-and-link`, body)
      .then(() => {
        setLinking(null)
        if (isWizard) {
          setLinkedIds(prev => new Set(prev).add(activeMaterialId))
          advanceToNext()
        } else {
          onLinked()
        }
      })
      .catch(err => { setLinking(null); setError(err.response?.data?.error || err.message) })
  }

  const handleMergeIntoQuote = (targetMaterial) => {
    setLinking(targetMaterial._id)
    setError(null)
    axios.post(`${url}/api/bid-estimation/projects/${projectId}/materials/${activeMaterialId}/merge-into/${targetMaterial._id}`)
      .then(() => {
        setLinking(null)
        if (isWizard) {
          setLinkedIds(prev => new Set(prev).add(activeMaterialId))
          advanceToNext()
        } else {
          onLinked()
        }
      })
      .catch(err => { setLinking(null); setError(err.response?.data?.error || err.message) })
  }

  const handleBulkAttachQuoteLines = async (items) => {
    setLinking('bulk-attach')
    setError(null)
    try {
      for (const item of items) {
        const vl = item._vl
        await axios.post(`${url}/api/bid-estimation/projects/${projectId}/materials/${activeMaterialId}/attach-quote-line`, {
          quoteId: vl.quoteId, lineIndex: vl.lineIndex
        })
      }
      setLinking(null)
      if (isWizard) {
        setLinkedIds(prev => new Set(prev).add(activeMaterialId))
        advanceToNext()
      } else {
        onLinked()
      }
    } catch (err) {
      setLinking(null)
      setError(err.response?.data?.error || err.message)
    }
  }

  const [duplicates, setDuplicates] = React.useState(null)

  const handleCreateNew = (data) => {
    setLinking('create-new')
    setError(null)
    setDuplicates(null)
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${activeMaterialId}/promote-and-link`, { createNew: data })
      .then(() => {
        setLinking(null)
        if (isWizard) {
          setLinkedIds(prev => new Set(prev).add(activeMaterialId))
          advanceToNext()
        } else {
          onLinked()
        }
      })
      .catch(err => {
        setLinking(null)
        if (err.response?.status === 409 && err.response?.data?.duplicates) {
          setDuplicates({ items: err.response.data.duplicates, originalData: data })
        } else {
          setError(err.response?.data?.error || err.message)
        }
      })
  }

  const handleForceCreate = () => {
    if (!duplicates?.originalData) return
    setLinking('create-new')
    setDuplicates(null)
    axios.put(`${url}/api/bid-estimation/projects/${projectId}/materials/${activeMaterialId}/promote-and-link`, { createNew: { ...duplicates.originalData, force: true } })
      .then(() => {
        setLinking(null)
        if (isWizard) {
          setLinkedIds(prev => new Set(prev).add(activeMaterialId))
          advanceToNext()
        } else {
          onLinked()
        }
      })
      .catch(err => { setLinking(null); setError(err.response?.data?.error || err.message) })
  }

  const handleWizardSkip = () => {
    if (onSkipMaterial) onSkipMaterial(activeMaterialId)
    setSkippedIds(prev => new Set(prev).add(activeMaterialId))
    advanceToNext()
  }

  const goToPrev = () => {
    for (let i = currentIdx - 1; i >= 0; i--) {
      const id = queue[i]?._id
      if (!linkedIds.has(id)) {
        setCurrentIdx(i)
        return
      }
    }
  }

  const goToNext = () => {
    for (let i = currentIdx + 1; i < queue.length; i++) {
      const id = queue[i]?._id
      if (!linkedIds.has(id) && !skippedIds.has(id)) {
        setCurrentIdx(i)
        return
      }
    }
  }

  const remaining = isWizard ? queue.filter(m => !linkedIds.has(m._id) && !skippedIds.has(m._id)).length : 0
  const hasPrev = isWizard && queue.slice(0, currentIdx).some(m => !linkedIds.has(m._id))
  const hasNext = isWizard && queue.slice(currentIdx + 1).some(m => !linkedIds.has(m._id) && !skippedIds.has(m._id))

  const quoteMatches = React.useMemo(() => {
    if (!activeMaterial || !projectMaterials.length) return []
    const myId = activeMaterialId
    const myText = [(activeMaterial.canonicalName || ''), (activeMaterial.notes || '')].join(' ').toLowerCase()
    const words = myText.split(/[\s\-\/,.()"*:@]+/).filter(w => w.length >= 3)
    if (!words.length) return []

    return projectMaterials
      .filter(pm => {
        if (pm._id === myId) return false
        if (!(pm.vendorOptions?.length > 0)) return false
        const pmText = [(pm.canonicalName || ''), ...(pm.vendorOptions || []).map(vo => vo.vendorDescription || '')].join(' ').toLowerCase()
        const matchCount = words.filter(w => pmText.includes(w)).length
        return matchCount >= 1
      })
      .map(pm => {
        const pmText = [(pm.canonicalName || ''), ...(pm.vendorOptions || []).map(vo => vo.vendorDescription || '')].join(' ').toLowerCase()
        const matchCount = words.filter(w => pmText.includes(w)).length
        return { ...pm, _matchScore: matchCount / words.length }
      })
      .sort((a, b) => b._matchScore - a._matchScore)
      .slice(0, 15)
  }, [activeMaterial, activeMaterialId, projectMaterials])

  const vendorLineMatches = React.useMemo(() => {
    if (!activeMaterial || !vendorQuoteLines.length) return []
    const myText = [(activeMaterial.canonicalName || ''), (activeMaterial.notes || '')].join(' ').toLowerCase()
    const words = myText.split(/[\s\-\/,.()"*:@]+/).filter(w => w.length >= 3)
    if (!words.length) return []

    const alreadyLinkedIds = new Set(
      projectMaterials.flatMap(pm => (pm.vendorOptions || []).map(vo => `${vo.vendorQuoteId}-${vo.vendorQuoteLineIdx}`))
    )

    return vendorQuoteLines
      .filter(vl => {
        if (alreadyLinkedIds.has(`${vl.quoteId}-${vl.lineIndex}`)) return false
        const vlText = [(vl.description || ''), (vl.vendorSku || ''), (vl.category || '')].join(' ').toLowerCase()
        return words.filter(w => vlText.includes(w)).length >= 1
      })
      .map(vl => {
        const vlText = [(vl.description || ''), (vl.vendorSku || ''), (vl.category || '')].join(' ').toLowerCase()
        const matchCount = words.filter(w => vlText.includes(w)).length
        return { ...vl, _matchScore: matchCount / words.length }
      })
      .sort((a, b) => b._matchScore - a._matchScore)
      .slice(0, 15)
  }, [activeMaterial, vendorQuoteLines, projectMaterials])

  const aspire = results.filter(r => r.source === 'aspire')
  const catalog = results.filter(r => r.source === 'catalog')
  const vendor = results.filter(r => r.source !== 'catalog' && r.source !== 'aspire')
  const totalMatches = results.length + quoteMatches.length + vendorLineMatches.length

  const sourceVendor = opt?.vendor?.toLowerCase() || ''
  const hasKnownVendorProduct = !!catalogProduct
  const vendorDefaultOpen = !hasKnownVendorProduct && vendor.length > 0

  React.useEffect(() => {
    const handleKey = (e) => {
      const tag = e.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (e.key === 'Escape') onClose()
      if (!isWizard || linking) return
      if (e.key === 'ArrowLeft' && hasPrev) { e.preventDefault(); goToPrev() }
      if (e.key === 'ArrowRight' && hasNext) { e.preventDefault(); goToNext() }
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose, isWizard, linking, hasPrev, hasNext, currentIdx])

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 16, maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogHeader material={activeMaterial} catalogProduct={catalogProduct} imgError={imgError} setImgError={setImgError} onClose={onClose} />

      <div className="dlg-search" style={{ flexShrink: 0 }}>
        <span className="dlg-overline">Search</span>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search across all catalogs at once..."
          value={search}
          onChange={e => { setSearch(e.target.value); setError(null) }}
          variant="outlined"
          style={{ marginTop: 4 }}
        />
        <div className="dlg-search-hint">Searching across all catalogs at once.</div>
      </div>

      {error && <div style={{ color: 'var(--risk)', fontSize: 12, padding: '0 24px 8px', flexShrink: 0 }}>{error}</div>}

      <DialogContent style={{ padding: 0, overflow: 'auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 20 }}><CircularProgress size={20} /></div>
        )}

        {!loading && quoteMatches.length > 0 && (
          <Group
            kind="quote"
            title="From your project quotes"
            subtitle="Vendor-quoted materials in this project that match — merge to combine quantities"
            items={quoteMatches.map(pm => ({
              _id: pm._id,
              canonicalName: pm.canonicalName,
              category: pm.category,
              unit: pm.unit,
              source: 'quote',
              bestPrice: pm.vendorOptions?.[0]?.unitPrice,
              vendorPriceHints: pm.vendorOptions?.map(vo => ({ vendor: vo.vendor, unitCost: vo.unitPrice })),
              vendorSku: pm.vendorOptions?.[0]?.vendorSku,
              _pm: pm
            }))}
            onSelect={(item) => handleMergeIntoQuote(item._pm)}
            linking={linking}
            defaultOpen
            empty=""
          />
        )}

        {!loading && vendorLineMatches.length > 0 && (
          <MultiSelectGroup
            kind="vendor"
            title="From your vendor quotes"
            subtitle="Select one or more vendor quote lines to attach as pricing options"
            items={vendorLineMatches.map(vl => ({
              _id: vl._id,
              canonicalName: vl.description || vl.vendorSku || '(no description)',
              category: vl.category || '',
              unit: vl.uom || '',
              source: 'vendor',
              bestPrice: vl.unitPrice,
              vendorPriceHints: [{ vendor: vl.vendor, unitCost: vl.unitPrice }],
              vendorSku: vl.vendorSku,
              _vl: vl
            }))}
            onBulkSelect={handleBulkAttachQuoteLines}
            linking={linking}
            defaultOpen
            empty=""
          />
        )}

        {!loading && search.trim().length >= 2 && (
          <>
            <Group kind="catalog" title="Our Material Catalog" subtitle="Best match — Aspire item linked, vendor pricing" items={catalog} onSelect={handleSelect} linking={linking}
              defaultOpen={catalog.length > 0}
              empty="No match in our catalog yet. Try Aspire or vendor catalogs below." />
            <Group kind="aspire" title="Aspire Catalog" subtitle="Corporate items — selecting promotes to our Material Catalog" items={aspire} onSelect={handleSelect} linking={linking} showPromote
              empty="No Aspire item matches — most landscape line items aren't in the corporate catalog." />
            <Group kind="vendor" title="Vendor Catalogs"
              subtitle={hasKnownVendorProduct ? `Already identified as ${sourceVendor} product — showing other vendor matches` : 'Ewing live, Aggregate Dashboard, AZ Wholesale Growers'}
              items={vendor} onSelect={handleSelect} linking={linking} showPromote
              defaultOpen={vendorDefaultOpen}
              empty={hasKnownVendorProduct ? `This item is already identified in the ${opt?.vendor} catalog. Link to an Aspire or Material Catalog item above.` : 'No live vendor catalog matches.'} />
            <CreateNewBlock material={activeMaterial} onCreateNew={handleCreateNew} linking={linking} totalMatches={totalMatches} aspireRef={aspireRef} />

            {duplicates && (
              <div className="create-block" style={{ borderStyle: 'solid', borderColor: 'var(--risk)', background: 'var(--bg-risk-soft, rgba(211,47,47,0.04))' }}>
                <span className="dlg-overline" style={{ color: 'var(--risk)' }}>Similar items already exist</span>
                <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 8 }}>
                  Link to one of these instead, or create anyway if this is truly a new item.
                </div>
                {duplicates.items.map(d => (
                  <div key={d._id} className="res-row" style={{ padding: '6px 0', borderBottom: '1px solid var(--line-1)' }}>
                    <div className="res-main" style={{ flex: 1 }}>
                      <div className="res-name">{d.canonicalName}</div>
                      <div className="res-meta">
                        <span>{cap(d.category)}</span>
                        <span className="dot-sep">·</span>
                        <span className="mono">{d.unit}</span>
                        {d.bestPrice != null && <><span className="dot-sep">·</span><span className="mono">{fmt$(d.bestPrice)}</span></>}
                      </div>
                    </div>
                    <button className="hmi-btn hmi-btn-primary hmi-btn-sm" onClick={() => handleSelect({ source: 'catalog', materialCatalogItemId: d._id, _id: d._id })}>
                      Link to this →
                    </button>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                  <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={() => setDuplicates(null)}>Cancel</button>
                  <button className="hmi-btn hmi-btn-outline hmi-btn-sm" onClick={handleForceCreate} disabled={!!linking}>
                    {linking === 'create-new' ? 'Creating...' : 'Create anyway →'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>

      <div className="dlg-foot">
        {isWizard ? (
          <>
            <div className="dlg-foot-nav">
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={goToPrev} disabled={!hasPrev || !!linking}>← Prev</button>
              <span className="dlg-foot-pos">
                {currentIdx + 1} of {queue.length}
                {(linkedIds.size > 0 || skippedIds.size > 0) && (
                  <span style={{ color: 'var(--ink-4)', marginLeft: 6 }}>
                    ({linkedIds.size} linked{skippedIds.size > 0 ? `, ${skippedIds.size} skipped` : ''}, {remaining} left)
                  </span>
                )}
              </span>
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={goToNext} disabled={!hasNext || !!linking}>Next →</button>
            </div>
            <div className="dlg-foot-actions">
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={handleWizardSkip} disabled={!!linking}>Skip this</button>
              <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <span className="dlg-foot-help">
              <span className="dlg-kbd">↑↓</span> navigate &nbsp; <span className="dlg-kbd">↵</span> link &nbsp; <span className="dlg-kbd">esc</span> close
            </span>
            <button className="hmi-btn hmi-btn-ghost hmi-btn-sm" onClick={onClose}>Cancel</button>
          </>
        )}
      </div>
    </Dialog>
  )
}

export default CatalogLinkDialog
